import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

const badgeVariants = cva(
  "flex w-fit items-center justify-center gap-1 rounded-md border px-2 py-0.75 text-xs font-medium whitespace-nowrap",
  {
    variants: {
      variant: {
        success: "border-[#ABEFC6] bg-[#ECFDF3] text-success",
        outline:
          "border-border-primary bg-new-foreground-secondary text-text-secondary",
        gradient: "border-transparent bg-primary-200 px-3 py-1 font-semibold",
        gradientSubtle: "border-transparent px-3 py-1 font-semibold",
        gradientGhost:
          "border-none bg-linear-to-br from-primary-400/20 to-secondary-400/20 px-3 py-1 font-semibold",
        tertiary:
          "rounded-full border-transparent bg-tertiary-400 px-2.5 font-medium text-white",
        tertiarySubtle:
          "border-tertiary-400/5 bg-tertiary-100 px-3 py-1 font-semibold text-tertiary-400",
      },
      size: {
        default: "h-5",
        sm: "rounded-full px-3",
        xs: "rounded-full px-2 py-0.5 text-[10px]",
      },
      defaultVariants: {
        variant: "success",
        size: "default",
      },
    },
  },
);

export const Dot = ({
  variant = "success",
  className,
}: {
  variant?: VariantProps<typeof badgeVariants>["variant"];
  className?: string;
}) => {
  return (
    <div
      className={cn(
        "h-1.5 w-1.5 rounded-full",
        {
          "bg-grey-400": variant === "outline",
          "bg-success": variant === "success",
        },
        className,
      )}
    />
  );
};

function Badge({
  className,
  variant,
  showDot = false,
  dotPosition = "leading",
  asChild = false,
  size,
  ...props
}: React.ComponentProps<"div"> &
  VariantProps<typeof badgeVariants> & {
    asChild?: boolean;
    showDot?: boolean;
    dotPosition?: "leading" | "trailing";
  }) {
  const Comp = asChild ? Slot : "div";
  const isGradient =
    variant === "gradient" ||
    variant === "gradientSubtle" ||
    variant === "gradientGhost";

  return (
    <Comp
      data-slot="badge"
      className={cn(badgeVariants({ variant, size }), className)}
      style={
        variant === "gradientSubtle"
          ? {
              background:
                "linear-gradient(to bottom right, var(--color-primary-100), var(--color-secondary-100)) padding-box, linear-gradient(to bottom right, color-mix(in srgb, var(--color-primary-400) 10%, transparent), color-mix(in srgb, var(--color-secondary-400) 10%, transparent)) border-box",
            }
          : undefined
      }
      {...props}
    >
      {showDot && !isGradient && dotPosition === "leading" && (
        <Dot variant={variant} className="mr-0.5" />
      )}
      {isGradient ? (
        <span className="inline-block bg-linear-to-r from-primary-400 to-secondary-400 bg-clip-text font-semibold text-transparent select-none">
          {props.children}
        </span>
      ) : (
        props.children
      )}
      {showDot && !isGradient && dotPosition === "trailing" && (
        <Dot variant={variant} className="ml-0.5" />
      )}
    </Comp>
  );
}

export { Badge, badgeVariants };
